import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PaginationBar } from "@/components/PaginationBar";
import { StatusPill } from "@/components/StatusBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { UserPlus, Copy, Check } from "lucide-react";
import CustomerRatesCard from "./finance/CustomerRatesCard";
import CustomerWalletCard from "./finance/CustomerWalletCard";

export default function CustomerDetail() {
  const { id } = useParams();
  const { user, tenant } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState({ items: [], pages: 1 });
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/customers/${id}`).then(({ data }) => setCustomer(data));
  }, [id]);
  useEffect(() => {
    api.get(`/customers/${id}/history`, { params: { page, limit: 15 } })
       .then(({ data }) => setHistory(data));
  }, [id, page]);

  if (!customer) return <div className="label-overline text-slate-500">Loading…</div>;

  const canManage = user && ["truckstop_owner", "truckstop_admin"].includes(user.role);

  return (
    <div>
      <div className="label-overline text-slate-500 flex items-center gap-2">
        <Link to="/app/customers">Customers</Link><span>/</span>
        <span className="font-mono">{customer.customer_code}</span>
      </div>
      <h1 className="text-3xl font-black tracking-tight mt-2 mb-6">{customer.display_name}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-overline mb-3">Details</div>
          <div className="text-sm grid grid-cols-2 gap-y-2">
            <div className="text-slate-500">Code</div><div className="font-mono">{customer.customer_code}</div>
            <div className="text-slate-500">Type</div><div>{customer.type}</div>
            {customer.company_name && (<>
              <div className="text-slate-500">Company</div><div>{customer.company_name}</div>
            </>)}
            {customer.vat_number && (<>
              <div className="text-slate-500">VAT</div><div className="font-mono">{customer.vat_number}</div>
            </>)}
            {customer.contact?.phone && (<>
              <div className="text-slate-500">Phone</div><div className="font-mono">{customer.contact.phone}</div>
            </>)}
          </div>
        </div>

        <div className="bg-white border border-slate-200 lg:col-span-2">
          <div className="px-4 py-3 border-b border-slate-200 label-overline">Operational history</div>
          <table className="tbl w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="label-overline text-slate-600">
                <th className="text-left px-4 py-2">Order</th>
                <th className="text-left px-4 py-2">Registration</th>
                <th className="text-left px-4 py-2">Service</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Opened</th>
              </tr>
            </thead>
            <tbody>
              {history.items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No orders yet</td></tr>
              )}
              {history.items.map((o) => (
                <tr key={o.id} className="border-b border-slate-100">
                  <td className="px-4 py-2 font-mono">
                    <Link to={`/app/orders/${o.id}`} className="text-blue-700 hover:underline">{o.order_no}</Link>
                  </td>
                  <td className="px-4 py-2 font-mono">{o.registration_no}</td>
                  <td className="px-4 py-2">{o.service_type_snapshot?.label}</td>
                  <td className="px-4 py-2"><StatusPill status={o.status} /></td>
                  <td className="px-4 py-2 font-mono text-xs">{fmtDate(o.opened_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationBar page={history.page || 1} pages={history.pages || 1} onChange={setPage} />
        </div>
      </div>

      {/* Stage 3B.1 — Customer Users card */}
      {canManage && (
        <div className="mt-4">
          <CustomerUsersCard customerId={id} customerName={customer.display_name} />
        </div>
      )}

      {/* Stage 3B.2 — Customer-specific rates card (financial plan only) */}
      {canManage && tenant?.feature_flags?.financial && (
        <div className="mt-4">
          <CustomerRatesCard customerId={id} customerName={customer.display_name} />
        </div>
      )}

      {/* Finance Flow P0 — Customer Wallet (financial plan only) */}
      {tenant?.feature_flags?.financial && (
        <div className="mt-4">
          <CustomerWalletCard customerId={id} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customer Users card — lists invited customer users + invite dialog
// ---------------------------------------------------------------------------

function CustomerUsersCard({ customerId, customerName }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/users", { params: {
      scope: "customer", role: "customer_user", limit: 100,
    }})
      .then(({ data }) => {
        // Backend returns all customer users of this tenant — filter to this
        // customer only. (Explicit safety even though the backend also scopes.)
        setUsers((data.items || [])
          .filter((u) => u.customer_account_id === customerId));
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  useEffect(load, [load]);

  return (
    <div className="bg-white border border-slate-200 p-5"
         data-testid="customer-users-card">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline">Customer users</div>
        <Button onClick={() => setShowInvite(true)}
                data-testid="customer-users-invite-btn"
                className="rounded bg-slate-900 hover:bg-slate-800 h-9 px-3">
          <UserPlus size={14} className="mr-2" />
          Invite user
        </Button>
      </div>
      <p className="text-xs text-slate-600 mb-4">
        Users invited here can log in to the customer portal and see only this
        customer&apos;s orders, receipts, and signed proof.
      </p>
      {loading ? (
        <div className="label-overline text-slate-500">Loading…</div>
      ) : users.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded p-6 text-center text-xs text-slate-500">
          No customer users yet. Click <b>Invite user</b> to add one.
        </div>
      ) : (
        <table className="tbl w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Last login</th>
              <th className="text-left px-3 py-2">Invited</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100"
                  data-testid={`customer-user-row-${u.email}`}>
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2">
                  <span className={"px-2 py-0.5 rounded text-[10px] font-bold " +
                    (u.is_active ? "bg-green-100 text-green-800"
                                 : "bg-slate-200 text-slate-700")}>
                    {u.is_active ? "active" : "inactive"}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-xs">
                  {u.last_login_at ? fmtDate(u.last_login_at) : "—"}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <InviteCustomerUserDialog
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onCreated={() => { setShowInvite(false); load(); }}
        customerId={customerId}
        customerName={customerName} />
    </div>
  );
}

function _randomPassword() {
  // Human-typable 14-char temp password with mixed case, digits and safe symbols
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 12; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out + "!" + Math.floor(Math.random() * 10);
}

function InviteCustomerUserDialog({ open, onClose, onCreated,
                                   customerId, customerName }) {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(""); setUsername(""); setPassword(_randomPassword());
      setCreated(null); setCopied(false);
    }
  }, [open]);

  const submit = async () => {
    if (!email.trim() || !password.trim()) {
      toast.error("Email and password required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/users", {
        email: email.trim().toLowerCase(),
        username: username.trim() || null,
        password,
        role: "customer_user",
        customer_account_id: customerId,
      });
      setCreated(data);
      toast.success("Customer user invited");
    } catch (e) {
      toast.error(formatError(e, "Could not invite user"));
    } finally { setSubmitting(false); }
  };

  const copy = () => {
    const text = `Customer portal login for ${customerName}\n\n` +
                 `URL: ${window.location.origin}/login\n` +
                 `Email: ${created.email}\n` +
                 `Temporary password: ${password}\n\n` +
                 `Please change your password after first login.`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success("Login details copied to clipboard");
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="invite-customer-user-dialog">
        <DialogHeader>
          <DialogTitle>Invite user for {customerName}</DialogTitle>
          <DialogDescription>
            The invited user will only see this customer&apos;s orders, receipts,
            and signed proof. They cannot access other customers or truck stop
            admin pages.
          </DialogDescription>
        </DialogHeader>

        {!created ? (
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Email <span className="text-red-600">*</span></Label>
              <Input type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid="invite-customer-user-email"
                     className="rounded h-10 mt-1"
                     placeholder="driver@bluestar.co.za" />
            </div>
            <div>
              <Label className="label-overline">Display name (optional)</Label>
              <Input value={username}
                     onChange={(e) => setUsername(e.target.value)}
                     data-testid="invite-customer-user-username"
                     className="rounded h-10 mt-1"
                     placeholder="John Driver" />
            </div>
            <div>
              <Label className="label-overline">Temporary password</Label>
              <div className="flex gap-2 mt-1">
                <Input value={password}
                       onChange={(e) => setPassword(e.target.value)}
                       data-testid="invite-customer-user-password"
                       className="rounded h-10 font-mono" />
                <Button variant="outline" type="button"
                        onClick={() => setPassword(_randomPassword())}
                        className="rounded h-10 whitespace-nowrap">
                  Regenerate
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Share this with the user securely. They should change it after first login.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-300 rounded p-4"
                 data-testid="invite-customer-user-success">
              <div className="label-overline text-green-800 mb-2">User invited</div>
              <div className="grid grid-cols-3 gap-y-2 text-sm">
                <div className="text-slate-500">Email</div>
                <div className="col-span-2 font-mono break-all">{created.email}</div>
                <div className="text-slate-500">Password</div>
                <div className="col-span-2 font-mono break-all">{password}</div>
                <div className="text-slate-500">Login URL</div>
                <div className="col-span-2 font-mono break-all text-xs">
                  {window.location.origin}/login
                </div>
              </div>
            </div>
            <Button onClick={copy} variant="outline" className="w-full rounded"
                    data-testid="invite-customer-user-copy">
              {copied ? <Check size={14} className="mr-2"/> : <Copy size={14} className="mr-2"/>}
              {copied ? "Copied" : "Copy login details"}
            </Button>
            <div className="text-[11px] text-slate-500 leading-relaxed">
              Send these details to the user through a secure channel (e.g.
              password manager, secure email). Password is never stored in
              plaintext on the server — this is your only chance to copy it.
            </div>
          </div>
        )}

        <DialogFooter>
          {!created ? (
            <>
              <Button variant="outline" onClick={onClose}
                      data-testid="invite-customer-user-cancel"
                      className="rounded">Cancel</Button>
              <Button onClick={submit} disabled={submitting}
                      data-testid="invite-customer-user-submit"
                      className="rounded bg-slate-900 hover:bg-slate-800">
                {submitting ? "Inviting…" : "Invite user"}
              </Button>
            </>
          ) : (
            <Button onClick={onCreated}
                    data-testid="invite-customer-user-done"
                    className="rounded bg-slate-900 hover:bg-slate-800">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
